import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import ChatInterface from '../components/ChatInterface'
import { TermsOfService } from './TermsOfService'
import { PrivacyPolicy } from './PrivacyPolicy'
import { SubscriptionGuard } from '../components/Subscription/SubscriptionGuard'
import { SubscriptionBanner } from '../components/Subscription/SubscriptionBanner'

type MainViewType = 'chat' | 'terms' | 'privacy'

export const ChatPage: React.FC = () => {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const [currentView, setCurrentView] = useState<MainViewType>('chat')

  // Handle email confirmation redirects
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    const type = urlParams.get('type')

    // If there are auth tokens in the URL, the user is being confirmed
    if (token && type) {
      // Clear the URL params after handling
      window.history.replaceState({}, document.title, '/chat')
    }
  }, [])

  // Redirect to landing page if not authenticated
  React.useEffect(() => {
    console.log('[ChatPage] Auth state - loading:', loading, 'user:', user?.email || 'No user')

    // Wait for auth to finish loading before making decision
    if (loading) {
      console.log('[ChatPage] Auth still loading, waiting...')
      return
    }

    // Only redirect if auth is done loading AND no user
    if (!user) {
      console.log('[ChatPage] Auth loaded, no user - redirecting to landing page')
      navigate('/')
    } else {
      console.log('[ChatPage] Auth loaded, user authenticated:', user.email)
    }
  }, [user, loading, navigate])

  // Show loading state while auth initializes
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
          <p className="text-light-secondary">Loading...</p>
        </div>
      </div>
    )
  }

  // Don't render anything if not authenticated (prevents flash during sign out)
  if (!user) {
    return null
  }

  const handleMessageSent = (message: string) => {
    console.log('BLP Message sent:', message);
  };

  const renderMainContent = () => {
    switch (currentView) {
      case 'terms':
        return <TermsOfService onBack={() => setCurrentView('chat')} />
      case 'privacy':
        return <PrivacyPolicy onBack={() => setCurrentView('chat')} />
      default:
        return (
          <SubscriptionGuard>
            <div className="h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary flex flex-col overflow-hidden">
              {/* Subscription Status Banner */}
              <SubscriptionBanner />

              {/* Full-Screen Chat Interface */}
              <ChatInterface
                placeholder="Ask questions about the BLP..."
                onMessageSent={handleMessageSent}
                className="flex-1"
                onNavigateToTerms={() => setCurrentView('terms')}
                onNavigateToPrivacy={() => setCurrentView('privacy')}
              />
            </div>
          </SubscriptionGuard>
        )
    }
  }

  return renderMainContent()
}